/**
 * AccountEditToggle — ⋯-Aktionsmenü in der Personen-Zeile des Familienkontos
 * (ehemals Zahnrad mit Inline-Panel).
 *
 * Menüpunkte:
 *   - "Familienkonto bearbeiten" → öffnet AccountForm + WohnenForm im
 *     EditDrawer (rechts einfahrend) statt des früheren Inline-Panels.
 *   - "Anfrage abbrechen" (rot, users.write) → Abbruch-Dialog für den
 *     Pflegeempfänger des Kontos.
 *   - Batch-12: bei bereits negativer Phase stattdessen "Anfrage
 *     wiederherstellen" — hebt den Abbruch auf (Phase zurück auf den Stand
 *     vor dem Abbruch).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditDrawer } from '../../../../components/ui/EditDrawer';
import { getThemeColors } from '../../../../lib/theme';
import { type FamilyAccount } from '../../../../lib/familyAccounts';
import { type V1ProfileOption, type AnfrageAbbruchResult } from '../../../../lib/v1';
import { usePermissions } from '../../../../contexts/PermissionsContext';
import { AnfrageAbbruchDialog } from '../../../anfragen/AnfrageAbbruchDialog';
import { v2AnfrageAbbruch } from '../../../../lib/v1';
import { toast } from '../../../../lib/toast';
import { AccountForm } from './AccountForm';
import { WohnenForm } from './WohnenForm';

export function AccountEditToggle({
  account, dark, onSaved, opts, peUserId, peName, pePhase, onCancelled, onRestored,
}: {
  account: FamilyAccount | null;
  dark: boolean;
  onSaved: (a: FamilyAccount) => void;
  opts: Record<string, V1ProfileOption[]> | null;
  /** User-ID des Pflegeempfängers (für "Anfrage abbrechen"); null = kein PE im Konto. */
  peUserId?: number | null;
  /** Anzeigename des Pflegeempfängers (Dialog-Untertitel). */
  peName?: string;
  /** Phase des PE, falls bekannt — < 0 deaktiviert den Abbruch-Eintrag. */
  pePhase?: number | null;
  /** Nach erfolgreichem Abbruch (Toast kommt von hier). */
  onCancelled?: (result: AnfrageAbbruchResult) => void;
  /** Batch-12: nach erfolgreicher Wiederherstellung (Toast kommt von hier). */
  onRestored?: (result: { id: number; phase: number }) => void;
}) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const { hasPermission } = usePermissions();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [abbruchOpen, setAbbruchOpen] = useState(false);
  // Batch-12: Wiederherstellen läuft direkt (ohne Dialog) — Busy-Guard.
  const [restoring, setRestoring] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleRestore = async () => {
    if (peUserId == null || restoring) return;
    setRestoring(true);
    try {
      const result = await v2AnfrageAbbruch.wiederherstellen(peUserId);
      setMenuOpen(false);
      toast(tr('anfragen.abbruch.restore_success'), 'success');
      onRestored?.(result);
    } catch {
      toast(tr('anfragen.abbruch.restore_failed'), 'error');
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  if (!account) return null;

  const canCancel = hasPermission('users.write') && peUserId != null;
  const isCancelled = typeof pePhase === 'number' && pePhase < 0;

  const itemStyle: React.CSSProperties = {
    display: 'block', width: '100%', textAlign: 'left',
    background: 'none', border: 'none', borderRadius: 6,
    padding: '7px 10px', cursor: 'pointer',
    fontSize: 12, fontWeight: 500, color: t.text,
    fontFamily: 'Outfit, sans-serif', whiteSpace: 'nowrap',
  };

  return (
    <>
      {/* ⋯-Button in der Personen-Zeile (ehemals Zahnrad). */}
      <div ref={menuRef} style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          type="button"
          onClick={() => setMenuOpen(o => !o)}
          aria-expanded={menuOpen}
          aria-label={tr('pflege.familienkonto.detail.account_actions')}
          title={tr('pflege.familienkonto.detail.account_actions')}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, flexShrink: 0,
            background: menuOpen ? (dark ? '#2a1222' : '#fce7f4') : 'transparent',
            border: `1px solid ${menuOpen ? '#b5297a' : t.border}`, borderRadius: 9999,
            cursor: 'pointer', color: menuOpen ? '#b5297a' : t.muted,
            fontSize: 16, lineHeight: 1, fontFamily: 'Outfit, sans-serif',
          }}
        >⋯</button>
        {menuOpen && (
          <div style={{
            position: 'absolute', left: 0, top: '110%', zIndex: 40,
            background: t.card, border: `1px solid ${t.border}`, borderRadius: 8,
            boxShadow: dark ? '0 6px 16px rgba(0,0,0,0.4)' : '0 6px 16px -4px rgba(16,24,40,0.14)',
            minWidth: 200, padding: 4,
          }}>
            <button
              onClick={() => { setMenuOpen(false); setDrawerOpen(true); }}
              style={itemStyle}
              onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = t.hover}
              onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = 'none'}
            >
              {tr('pflege.familienkonto.detail.edit_account')}
            </button>
            {canCancel && (
              isCancelled ? (
                // Batch-12: Abbruch aufheben — Phase springt zurück auf den
                // Stand vor dem Abbruch.
                <button
                  onClick={() => void handleRestore()}
                  disabled={restoring}
                  style={{ ...itemStyle, color: '#027a48', fontWeight: 600, opacity: restoring ? 0.6 : 1 }}
                  onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = t.hover}
                  onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = 'none'}
                >
                  {restoring ? tr('common.loading_short') : tr('anfragen.abbruch.restore_action')}
                </button>
              ) : (
                <button
                  onClick={() => { setMenuOpen(false); setAbbruchOpen(true); }}
                  style={{ ...itemStyle, color: '#b42318', fontWeight: 600 }}
                  onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = t.hover}
                  onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = 'none'}
                >
                  {tr('anfragen.abbruch.action')}
                </button>
              )
            )}
          </div>
        )}
      </div>

      {/* Konto-Bearbeitung als Drawer (ehemals Inline-Panel unter der Zeile). */}
      <EditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={tr('pflege.familienkonto.detail.edit_account')}
        dark={dark}
        width={520}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
          {tr('pflege.familienkonto.detail.account_section')}
        </div>
        <AccountForm dark={dark} account={account} onSaved={a => { onSaved(a); }} compact />
        <div style={{ marginTop: 20, borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
            Wohnen &amp; Umfeld
          </div>
          <WohnenForm dark={dark} account={account} opts={opts} onSaved={onSaved} />
        </div>
      </EditDrawer>

      {/* Anfrage abbrechen — für den Pflegeempfänger des Kontos. */}
      {peUserId != null && (
        <AnfrageAbbruchDialog
          open={abbruchOpen}
          dark={dark}
          scope="pb"
          anfrageName={peName}
          onClose={() => setAbbruchOpen(false)}
          onConfirm={async (grundId, beschreibung) => {
            const result = await v2AnfrageAbbruch.abbrechen(peUserId, {
              grund_id: grundId,
              ...(beschreibung ? { beschreibung } : {}),
            });
            setAbbruchOpen(false);
            toast(tr('anfragen.abbruch.success'), 'success');
            onCancelled?.(result);
          }}
        />
      )}
    </>
  );
}
